/**
 * Hook pour verifier les permissions sur les documents - CORE-12
 */
import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  checkDocumentPermission,
  isNonModifiableType,
  isCentralisGeneratedType,
  canModifyNonModifiable,
  DOCUMENT_MESSAGES,
  type DocumentPermission,
} from '../config/documentConfig';

interface UseDocumentPermissionsReturn {
  /**
   * Verifie les permissions pour un type de document
   */
  checkPermission: (type: string, documentExists: boolean) => DocumentPermission;
  
  /**
   * Verifie si le type est non modifiable
   */
  isNonModifiable: (type: string) => boolean;
  
  /**
   * Verifie si le type est genere par Centralis
   */
  isCentralisGenerated: (type: string) => boolean;
  
  /**
   * Verifie si l'utilisateur est de l'equipe Centralis
   */
  isCentralisUser: boolean;
  
  /**
   * Messages predefinies
   */
  messages: typeof DOCUMENT_MESSAGES;
}

/**
 * Hook pour gerer les permissions des documents
 */
export const useDocumentPermissions = (): UseDocumentPermissionsReturn => {
  const { user } = useAuth();

  // Verifie si l'utilisateur est de l'equipe Centralis
  const isCentralisUser = useMemo(() => {
    return canModifyNonModifiable(user?.role);
  }, [user?.role]);

  // Fonction pour verifier les permissions
  const checkPermission = useCallback(
    (type: string, documentExists: boolean): DocumentPermission => {
      return checkDocumentPermission(type, documentExists, user?.role);
    },
    [user?.role]
  );

  // Fonction pour verifier si non modifiable
  const isNonModifiable = useCallback((type: string): boolean => {
    return isNonModifiableType(type);
  }, []);

  // Fonction pour verifier si genere par Centralis
  const isCentralisGenerated = useCallback((type: string): boolean => {
    return isCentralisGeneratedType(type);
  }, []);

  return {
    checkPermission,
    isNonModifiable,
    isCentralisGenerated,
    isCentralisUser,
    messages: DOCUMENT_MESSAGES,
  };
};

export default useDocumentPermissions;
